import { InboxNotification, Notification, NotificationKey } from 'models';

import type { NotificationCounts } from '@/collections/notifications/endpoints/counts';

enum NotificationCategory {
    MESSAGES = 'messages',
    ACCESS = 'access',
    COMPANY = 'company',
    CREDITS = 'credits',
    OTHER = 'other',
}

const CATEGORY_BY_TYPE: Record<NotificationKey, NotificationCategory> = {
    [NotificationKey.DIAGRAM_CHAT_MESSAGE]: NotificationCategory.MESSAGES,
    [NotificationKey.INTERCOM_MESSAGE]: NotificationCategory.MESSAGES,
    [NotificationKey.DIAGRAM_CHAT_REACTION]: NotificationCategory.MESSAGES,
    [NotificationKey.INTERCOM_REACTION]: NotificationCategory.MESSAGES,
    [NotificationKey.ORDER_MESSAGE]: NotificationCategory.MESSAGES,
    [NotificationKey.DIAGRAM_COMMENT_CREATE]: NotificationCategory.MESSAGES,
    [NotificationKey.DIAGRAM_COMMENT_REPLY_USER]: NotificationCategory.MESSAGES,
    [NotificationKey.DIAGRAM_COMMENT_REPLY_AI]: NotificationCategory.MESSAGES,
    [NotificationKey.DIAGRAM_COMMENT_MENTION_USER]: NotificationCategory.MESSAGES,
    [NotificationKey.DIAGRAM_COMMENT_MENTION_MANUFACTURER]: NotificationCategory.MESSAGES,
    [NotificationKey.DIAGRAM_INVITE]: NotificationCategory.ACCESS,
    [NotificationKey.USER_TEAM_INVITE]: NotificationCategory.ACCESS,
    [NotificationKey.DIAGRAM_REQUESTACCESS]: NotificationCategory.ACCESS,
    [NotificationKey.COMPANY_REQUEST_ACCESS]: NotificationCategory.ACCESS,
    [NotificationKey.TEAM_REQUEST_ACCESS]: NotificationCategory.ACCESS,
    [NotificationKey.COMPANY_GRANT_ACCESS]: NotificationCategory.ACCESS,
    [NotificationKey.TEAM_GRANT_ACCESS]: NotificationCategory.ACCESS,
    [NotificationKey.COMPANY_DENY_ACCESS]: NotificationCategory.ACCESS,
    [NotificationKey.TEAM_DENY_ACCESS]: NotificationCategory.ACCESS,
    [NotificationKey.DIAGRAM_CHAT_CHANNEL]: NotificationCategory.COMPANY,
    [NotificationKey.COMPANY_ADDED_AS_PARTNER]: NotificationCategory.COMPANY,
    [NotificationKey.COMPANY_NEW_EXHIBITOR_MATCH_LEAD]: NotificationCategory.COMPANY,
    [NotificationKey.COMPANY_NEW_QR_SCAN]: NotificationCategory.COMPANY,
    [NotificationKey.DIAGRAM_CHAT_CREDITS_ESTIMATE]: NotificationCategory.CREDITS,
    [NotificationKey.DIAGRAM_CHAT_CREDITS_ACCEPT]: NotificationCategory.CREDITS,
    [NotificationKey.DIAGRAM_CHAT_CREDITS_DECLINE]: NotificationCategory.CREDITS,
    [NotificationKey.DIAGRAM_CHAT_CREDITS_CHARGE]: NotificationCategory.CREDITS,
    [NotificationKey.ORDER_EVENT]: NotificationCategory.OTHER,
    [NotificationKey.DESIGN_DUPLICATE]: NotificationCategory.OTHER,
};

type Count = { total: number; unread: number };
type CategoryCounts = { all: Count; byCategory: Record<NotificationCategory, Count> };

type InboxQuery = { types?: string[] };
type LocalInboxQuery = InboxQuery & { team?: string | null; company?: string | null; unread?: boolean };

const NotificationHelpers = {
    getQuery: (category: NotificationCategory | null): InboxQuery =>
        category ? { types: Object.values(NotificationKey).filter((type) => CATEGORY_BY_TYPE[type] === category) } : {},

    matchesQuery: (notification: InboxNotification, query: LocalInboxQuery) =>
        (!query.types || query.types.includes(notification.type)) &&
        (!query.unread || !notification.readAt) &&
        (!query.team || notification.team === query.team),

    isMessage: (notification: Notification) => CATEGORY_BY_TYPE[notification.type] === NotificationCategory.MESSAGES,

    getCategoryCounts: (countsByType: NotificationCounts['byType']): CategoryCounts => {
        const categoryCounts: CategoryCounts = {
            all: { total: 0, unread: 0 },
            byCategory: Object.fromEntries(
                Object.values(NotificationCategory).map((category) => [category, { total: 0, unread: 0 }]),
            ) as CategoryCounts['byCategory'],
        };

        for (const [type, count] of Object.entries(countsByType)) {
            const category = CATEGORY_BY_TYPE[type as NotificationKey];

            categoryCounts.all = addCounts(categoryCounts.all, count);

            if (category) {
                categoryCounts.byCategory[category] = addCounts(categoryCounts.byCategory[category], count);
            }
        }

        return categoryCounts;
    },

    getSender: (notification: Notification): { user?: string; company?: string } | null => {
        switch (notification.type) {
            case NotificationKey.COMPANY_NEW_EXHIBITOR_MATCH_LEAD:
            case NotificationKey.COMPANY_GRANT_ACCESS:
            case NotificationKey.COMPANY_DENY_ACCESS:
                return { company: notification.data.companyId };
            case NotificationKey.COMPANY_ADDED_AS_PARTNER:
                return { company: notification.data.addedByCompanyId };
        }

        if (notification.fromCompany) {
            return { company: notification.fromCompany };
        }

        return notification.from ? { user: notification.from } : null;
    },
};

const addCounts = (first: Count, second: Count): Count => ({
    total: first.total + second.total,
    unread: first.unread + second.unread,
});

export type { Count, InboxQuery };
export { NotificationCategory, NotificationHelpers };
